/**
 * Catálogo de módulos da sala (GetUIRoomModuleClassID no binário).
 */
import type { ReactNode } from 'react';
import { MusicPlayerModule } from '../audio/MusicPlayerModule.js';
import { SfxPlayerModule } from '../audio/SfxPlayerModule.js';
import { useT } from '../../i18n/index.js';
import { Icon, type IconName } from '../../ui/icons.js';
import { useApp } from '../../store.js';
import type { ModuleClassId, Sheet } from '../layout.js';
import type { LibraryItem, RoomPlugin } from '../../shared.js';
import { ChatModule, DiceSourceMark } from './ChatModule.js';
import { MACROS_PANEL, MacrosPanel } from '../macros/MacrosPanel.js';
import { GroupChatModule, PvtChatModule } from './PrivateChatModules.js';
import { SheetModule } from '../../plugins/SheetModule.js';
import { SceneModule } from '../../scene/SceneModule.js';
import { CombatTracker } from '../combat/CombatTracker.js';
import { COMBAT_TRACKER } from '../combat/model.js';
import { LibraryModule } from './LibraryModule.js';
import { RoomFilesModule } from '../../cloud/RoomFilesModule.js';
import { SessionsModule } from '../clock/SessionsModule.js';
import { RoomSettingsModule } from './RoomSettingsModule.js';
import { UserListModule } from './UserListModule.js';

export type ModuleDef = {
  classId: ModuleClassId;
  titleKey: string;
  /** aparece no menu Abas e no "+" da faixa de abas (docs/spec/room-menus.md) */
  addable: boolean;
  /** o ícone na aba, no menu Abas e no "Mais" do celular */
  icon: IconName;
  /** pode haver mais de uma instância */
  multiple: boolean;
  render: (sheet: Sheet) => ReactNode;
};

/** Tabela da sala: as embutidas do RPG Desk (tracker de combate, macros) ou o form de um plugin. */
function TableDock({ itemId }: { itemId: string }) {
  const dataType = useApp((s) => s.library.find((i) => i.id === itemId)?.dataType);
  if (dataType === COMBAT_TRACKER) return <CombatTracker itemId={itemId} />;
  if (dataType === MACROS_PANEL) return <MacrosPanel />;
  return <SheetModule itemId={itemId} />;
}

function Placeholder({ text }: { text: string }) {
  return <div className="rd-module__placeholder">{text}</div>;
}

export const MODULES: Record<ModuleClassId, ModuleDef> = {
  'system/mainChat': { classId: 'system/mainChat', titleKey: 'module.mainChat', addable: false, icon: 'chat', multiple: false, render: () => <ChatModule /> },
  'system/stdVertUserList': {
    classId: 'system/stdVertUserList',
    titleKey: 'module.userList',
    addable: true,
    icon: 'rooms',
    multiple: false,
    render: () => <UserListModule />,
  },
  'system/roomLibrary': {
    classId: 'system/roomLibrary',
    titleKey: 'module.library',
    addable: true,
    icon: 'folder',
    multiple: false,
    render: () => <LibraryModule />,
  },
  // cena da biblioteca (param = id do item)
  'system/scene': {
    classId: 'system/scene',
    titleKey: 'module.scene',
    addable: false,
    icon: 'map',
    multiple: true,
    render: (sheet) => (sheet.param ? <SceneModule key={sheet.param} itemId={sheet.param} /> : <Placeholder text="—" />),
  },
  'system/musicPlayer': { classId: 'system/musicPlayer', titleKey: 'module.music', addable: true, icon: 'music', multiple: false, render: () => <MusicPlayerModule /> },
  'system/sfxPlayer': { classId: 'system/sfxPlayer', titleKey: 'module.sfx', addable: true, icon: 'volume', multiple: false, render: () => <SfxPlayerModule /> },
  'system/roomFiles': { classId: 'system/roomFiles', titleKey: 'module.files', addable: true, icon: 'cloud', multiple: false, render: () => <RoomFilesModule /> },
  // a aba Sessões (docs/spec/session-clock.md): a sessão aberta e o registro das anteriores
  'system/gameSessions': { classId: 'system/gameSessions', titleKey: 'module.gameSessions', addable: true, icon: 'clock', multiple: false, render: () => <SessionsModule /> },
  // tabela da sala (TablesDock): form de plugin `tablesDock` ligado ao item "dock" (param = id do item)
  'system/tableDock': {
    classId: 'system/tableDock',
    titleKey: 'module.tableDock',
    addable: false,
    icon: 'bolt',
    multiple: true,
    render: (sheet) => (sheet.param ? <TableDock key={sheet.param} itemId={sheet.param} /> : <Placeholder text="—" />),
  },
  'system/roomSettings': { classId: 'system/roomSettings', titleKey: 'module.roomSettings', addable: false, icon: 'settings', multiple: false, render: () => <RoomSettingsModule /> },
  'system/pvtChat': {
    classId: 'system/pvtChat',
    titleKey: 'module.pvtChat',
    addable: false,
    icon: 'chat',
    multiple: true,
    render: (sheet) => (sheet.param ? <PvtChatModule key={sheet.param} userId={sheet.param} /> : null),
  },
  'system/groupPVTChat': {
    classId: 'system/groupPVTChat',
    titleKey: 'module.groupPvtChat',
    addable: false,
    icon: 'chat',
    multiple: true,
    render: (sheet) => (sheet.param ? <GroupChatModule key={sheet.param} groupId={sheet.param} /> : null),
  },
  // ficha de plugin (web) de um item da biblioteca
  'system/charSheet': {
    classId: 'system/charSheet',
    titleKey: 'module.charSheet',
    addable: false,
    icon: 'sheet',
    multiple: true,
    render: (sheet) => (sheet.param ? <SheetModule key={sheet.param} itemId={sheet.param} /> : <Placeholder text="—" />),
  },
};

type Translate = (key: string, params?: Record<string, string | number>) => string;

/** O título da aba em texto, para os menus: o mesmo do `SheetTitle` (sem a marca dos dados no chat). */
export function sheetText(sheet: Sheet, t: Translate): string {
  const s = useApp.getState();
  switch (sheet.classId) {
    case 'system/pvtChat': {
      const name = sheet.param ? (s.members[sheet.param]?.nick ?? s.users.find((u) => u.id === sheet.param)?.displayName) : null;
      return t('module.pvtChat.titleFmt', { name: name ?? '…' });
    }
    case 'system/tableDock': {
      const item = s.library.find((i) => i.id === sheet.param);
      return item ? tableName(item, t, s.plugins) : t('module.tableDock');
    }
    case 'system/roomSettings': {
      const canAdminister = !!s.room && !!s.user && (s.room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes('gm'));
      return t(canAdminister ? 'room.settings' : 'room.settings.plugins');
    }
    case 'system/charSheet':
    case 'system/scene':
      return s.library.find((i) => i.id === sheet.param)?.name ?? t(MODULES[sheet.classId].titleKey);
    default:
      return t(MODULES[sheet.classId].titleKey);
  }
}

/**
 * O que a aba mostra na faixa (na mesa, no acordeão e na janela própria de um espaço): o ícone do
 * módulo e o título. `id` vai no nome, e o espaço da aba à vista é chamado por ele
 * (`aria-labelledby`).
 */
export function TabLabel({ sheet, id }: { sheet: Sheet; id?: string }) {
  return (
    <>
      <span className="rd-dock__tabIcon" aria-hidden="true">
        <Icon name={MODULES[sheet.classId].icon} size={14} />
      </span>
      <span id={id} className="rd-dock__tabName">
        <SheetTitle sheet={sheet} />
      </span>
    </>
  );
}

/** Título da aba: fixo pelo módulo, ou o nome da pessoa na conversa privada. */
export function SheetTitle({ sheet }: { sheet: Sheet }) {
  const t = useT();
  const name = useApp((s) => {
    if (sheet.classId !== 'system/pvtChat' || !sheet.param) return null;
    return s.members[sheet.param]?.nick ?? s.users.find((u) => u.id === sheet.param)?.displayName ?? null;
  });
  if (sheet.classId === 'system/pvtChat') return <>{t('module.pvtChat.titleFmt', { name: name ?? '…' })}</>;
  if (sheet.classId === 'system/tableDock') return <TableTitle itemId={sheet.param} />;
  if (sheet.classId === 'system/roomSettings') return <RoomSettingsTitle />;
  if (sheet.classId === 'system/charSheet' || sheet.classId === 'system/scene') return <SheetTitleName itemId={sheet.param} fallback={MODULES[sheet.classId].titleKey} />;
  if (sheet.classId === 'system/mainChat')
    return (
      <>
        {t(MODULES[sheet.classId].titleKey)}
        <DiceSourceMark />
      </>
    );
  return <>{t(MODULES[sheet.classId].titleKey)}</>;
}

/**
 * Nome de uma tabela da sala na língua de quem vê: o item guarda o nome na língua de quem a
 * ativou, e as do RPG Desk e as dos plugins têm o título traduzido pelo tipo.
 */
export function tableName(item: LibraryItem, t: (key: string) => string, plugins: readonly RoomPlugin[]): string {
  if (item.dataType === COMBAT_TRACKER) return t('combat.title');
  if (item.dataType === MACROS_PANEL) return t('macro.panel');
  for (const plugin of plugins) for (const type of plugin.dataTypes) if (type.id === item.dataType && type.title) return type.title;
  return item.name;
}

/** Quem não administra a mesa só vê a página Plugins: a aba diz isso. */
function RoomSettingsTitle() {
  const t = useT();
  const canAdminister = useApp((s) => !!s.room && !!s.user && (s.room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes('gm')));
  return <>{t(canAdminister ? 'room.settings' : 'room.settings.plugins')}</>;
}

function TableTitle({ itemId }: { itemId: string | undefined }) {
  const t = useT();
  const name = useApp((s) => {
    const item = s.library.find((i) => i.id === itemId);
    return item ? tableName(item, t, s.plugins) : null;
  });
  return <>{name ?? t('module.tableDock')}</>;
}

function SheetTitleName({ itemId, fallback }: { itemId: string | undefined; fallback: string }) {
  const t = useT();
  const name = useApp((s) => s.library.find((i) => i.id === itemId)?.name);
  return <>{name ?? t(fallback)}</>;
}
